import type { FenceConvergenceClaim } from "./fence-store";

// The convention half of the fence's capture side: what a GraphQL response
// carries is discovered by SHAPE, never by per-operation code — any
// `FencedMutationPayload` mints a `writeToken: String` beside its record,
// and any fence-capable answer discloses through a `convergence` object
// (`ConvergenceMeta`'s claim booleans). Walking the response for those two
// names is what keeps new minting mutations and new fenced reads at zero
// client wiring.
//
// The walk stops at discovery: each capture hands back the token-bearing
// payload node VERBATIM, and reading domain associations out of it (which
// entity the token vouches for, which containers it fences) belongs to the
// Apollo capture layer that knows the payload family — this platform
// module never learns a domain's payload shape (workspace taxonomy:
// business types stay out of platform packages).

export type FenceHarvestCapture = {
  token: string;
  /**
   * The `writeToken`-bearing payload node, verbatim (a reference into the
   * response tree, read-only by convention) — the capture layer's payload
   * readers derive domain associations from it.
   */
  payload: Record<string, unknown>;
  /** Disclosures found INSIDE the payload subtree — the self-fenced signal. */
  disclosures: FenceConvergenceClaim[];
};

export type FenceHarvest = {
  /** One entry per `writeToken`-bearing payload object in the response. */
  captures: FenceHarvestCapture[];
  /** Every convergence disclosure anywhere in the response. */
  disclosures: FenceConvergenceClaim[];
};

// Responses are JSON trees; the cap only bounds a pathological input.
const MAX_DEPTH = 32;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const asClaim = (value: unknown): FenceConvergenceClaim | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }
  const { fenced, converged } = value;
  if (typeof fenced !== "boolean" || typeof converged !== "boolean") {
    return undefined;
  }
  return { fenced, converged };
};

/**
 * Walk a GraphQL response's `data` tree and collect the fence signals it
 * carries: minted `writeToken`s (each with its payload node and the
 * disclosures inside it) and every `convergence` disclosure. Pure and
 * total — an unexpected shape contributes nothing rather than throwing.
 */
export const harvestFenceSignals = (data: unknown): FenceHarvest => {
  const captures: FenceHarvestCapture[] = [];
  const disclosures: FenceConvergenceClaim[] = [];

  const walk = (node: unknown, depth: number, capture: FenceHarvestCapture | undefined): void => {
    if (depth > MAX_DEPTH) {
      return;
    }
    if (Array.isArray(node)) {
      node.forEach((element) => walk(element, depth + 1, capture));
      return;
    }
    if (!isRecord(node)) {
      return;
    }

    let enclosing = capture;
    const token = node["writeToken"];
    if (typeof token === "string" && token) {
      enclosing = {
        token,
        payload: node,
        disclosures: [],
      };
      captures.push(enclosing);
    }

    Object.entries(node).forEach(([key, value]) => {
      if (key === "convergence") {
        const claim = asClaim(value);
        if (claim) {
          disclosures.push(claim);
          enclosing?.disclosures.push(claim);
          return;
        }
      }
      walk(value, depth + 1, enclosing);
    });
  };

  walk(data, 0, undefined);
  return { captures, disclosures };
};
